import { Button } from '@/components/ui/button';
import type { ApiError } from '@/types/api/api-error';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { PageHeader } from './primitives';

interface AdminFormPageProps {
  backTo: string;
  backLabel: string;
  eyebrow: string;
  title: string;
  description: string;
  headerAction?: ReactNode;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  onCancel: () => void;
  saveLabel: string;
  saving?: boolean;
  /** Exibe "Excluir" → "Confirmar exclusão" (dois cliques). */
  onDelete?: () => void;
  children: ReactNode;
}

export function AdminFormPage({
  backTo,
  backLabel,
  eyebrow,
  title,
  description,
  headerAction,
  onSubmit,
  onCancel,
  saveLabel,
  saving,
  onDelete,
  children,
}: AdminFormPageProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDelete = () => {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    onDelete?.();
  };

  return (
    <form noValidate onSubmit={onSubmit} className="flex w-full flex-col gap-5">
      <Link
        to={backTo}
        className="flex items-center gap-1.5 self-start text-[13px] font-semibold text-muted transition-colors hover:text-brand"
      >
        <ChevronLeft className="size-4" />
        {backLabel}
      </Link>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
        actions={headerAction}
      />

      {children}

      <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
        <div className="flex items-center gap-3">
          {onDelete && (
            <Button
              type="button"
              size="lg"
              variant="danger"
              onClick={handleDelete}
              onBlur={() => setConfirmDelete(false)}
            >
              {confirmDelete ? 'Confirmar exclusão' : 'Excluir'}
            </Button>
          )}
          <span className="text-[13px] text-muted">* Campos obrigatórios</span>
        </div>
        <div className="flex gap-3">
          <Button type="button" size="lg" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" size="lg" disabled={saving}>
            {saveLabel}
          </Button>
        </div>
      </div>
    </form>
  );
}

/** Aplica no formulário os erros de validação devolvidos pela API. */
export function useServerErrors<T extends FieldValues>(
  form: UseFormReturn<T>,
  error: ApiError | null,
) {
  useEffect(() => {
    if (!error?.errors || typeof error.errors !== 'object') return;
    Object.entries(error.errors).forEach(([field, messages]) => {
      const message = Array.isArray(messages) ? messages[0] : messages;
      if (message) form.setError(field as Path<T>, { message: String(message) });
    });
  }, [error]);
}
